import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import Button from "@/components/ui/button";
import MapComponent from "@/components/ui/map";
import AddressSearch from "./AddressSearch";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import type { PresetLocationApi } from "@/features/education-management/types/education-management.types";
import styles from "./CreateLocationModal.module.scss";

interface CreateLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (location: PresetLocationApi) => void;
  /** Düzenleme modu için mevcut konum (varsa PATCH yapılır) */
  editing?: PresetLocationApi | null;
}

const CreateLocationModal: React.FC<CreateLocationModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  editing,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (editing) {
      setName(editing.name ?? "");
      setAddress(editing.address ?? "");
      setLatitude(editing.latitude != null ? String(editing.latitude) : "");
      setLongitude(editing.longitude != null ? String(editing.longitude) : "");
    } else {
      setName("");
      setAddress("");
      setLatitude("");
      setLongitude("");
    }
  }, [isOpen, editing]);

  const reset = () => {
    setName("");
    setAddress("");
    setLatitude("");
    setLongitude("");
  };

  const handleClose = () => {
    if (isSaving) return;
    reset();
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(
        t("createLocation.nameRequired"),
      );
      return;
    }
    const lat = latitude ? Number(latitude) : undefined;
    const lng = longitude ? Number(longitude) : undefined;
    if (
      (lat != null && (Number.isNaN(lat) || lat < -90 || lat > 90)) ||
      (lng != null && (Number.isNaN(lng) || lng < -180 || lng > 180))
    ) {
      toast.error(
        t("createLocation.coordsInvalid"),
      );
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: name.trim(),
        address: address.trim() || undefined,
        latitude: lat,
        longitude: lng,
      };
      const saved = editing
        ? await educationManagementApi.updateLocation(editing.id, payload)
        : await educationManagementApi.createLocation(payload);
      toast.success(
        editing
          ? t("createLocation.updateSuccess")
          : t("createLocation.success"),
      );
      onCreated({
        id: saved.id,
        name: saved.name,
        address: saved.address ?? "",
        latitude: saved.latitude ?? null,
        longitude: saved.longitude ?? null,
      });
      reset();
      onClose();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t("createLocation.error");
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        editing
          ? t("createLocation.editTitle")
          : t("createLocation.title")
      }
      size="md"
      loading={isSaving}
      customFooter={
        <div className={styles.footer}>
          <Button
            variant="text"
            color="secondary"
            onClick={handleClose}
            disabled={isSaving}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleSave()}
            loading={isSaving}
            disabled={isSaving}
          >
            {t("common.save")}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        <AddressSearch
          label={t("createLocation.searchAddress")}
          onSelect={(r) => {
            setLatitude(String(r.lat));
            setLongitude(String(r.lng));
            setAddress(r.display);
          }}
        />

        <Input
          label={t("createLocation.name")}
          placeholder={t("createLocation.namePlaceholder")}
          value={name}
          maxLength={200}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <div className={styles.mapLabel}>
          {t("createLocation.mapHint")}
        </div>
        <MapComponent
          center={
            latitude && longitude
              ? { lat: Number(latitude), lng: Number(longitude) }
              : { lat: 41.0082, lng: 28.9784 }
          }
          zoom={latitude && longitude ? 14 : 10}
          height="260px"
          onLocationSelect={({ lat, lng, address: addr }) => {
            setLatitude(String(lat));
            setLongitude(String(lng));
            if (addr) setAddress(addr);
          }}
          selectedLocation={
            latitude && longitude
              ? {
                  lat: Number(latitude),
                  lng: Number(longitude),
                  address: address || "",
                }
              : undefined
          }
        />
      </div>
    </Modal>
  );
};

export default CreateLocationModal;
